import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../components/ui/card';
import { Loader2, Mail, ArrowLeft } from 'lucide-react';

const NEUTRAL_CONFIRMATION =
  "If an account exists for that email, we've sent a reset link.";

function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    // Fire the reset request but always show the same confirmation —
    // we intentionally do not surface whether the account exists.
    try {
      await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
    } catch (err) {
      // Swallow — response is uniform regardless of outcome.
      console.error('resetPasswordForEmail error:', err);
    }

    setLoading(false);
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0a0a0a] via-[#1a1a2e] to-[#0a0a0a] p-4">
      <Card className="w-full max-w-md bg-white/5 border-white/10 backdrop-blur-lg">
        <CardHeader className="text-center">
          <div className="font-mono text-[#00d4aa] text-sm uppercase tracking-wider mb-2">
            EPSILLON MEDIA
          </div>
          <CardTitle className="text-3xl font-bold text-white">
            Reset Your Password
          </CardTitle>
          <CardDescription className="text-white/60">
            Enter your email and we&apos;ll send you a reset link
          </CardDescription>
        </CardHeader>
        <CardContent>
          {submitted ? (
            <div className="space-y-4">
              <div
                role="status"
                className="rounded-md border border-[#00d4aa]/30 bg-[#00d4aa]/10 p-4 text-center text-white/90"
              >
                {NEUTRAL_CONFIRMATION}
              </div>
              <div className="text-center text-sm text-white/60">
                <Link
                  to="/login"
                  className="text-[#00d4aa] hover:underline inline-flex items-center"
                >
                  <ArrowLeft className="w-3 h-3 mr-1" />
                  Back to sign in
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email" className="text-white/80">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-white/5 border-white/10 text-white mt-2"
                  placeholder="you@example.com"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-[#00d4aa] hover:bg-[#00e6bb] text-black font-semibold"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Send reset link
                  </>
                )}
              </Button>

              <div className="text-center text-sm text-white/60">
                Remembered your password?{' '}
                <Link to="/login" className="text-[#00d4aa] hover:underline">
                  Sign in
                </Link>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default ForgotPassword;
